/**
 * Application Routes Constants
 *
 * Centralized route definitions for the application.
 * Use these constants instead of hardcoded route strings to:
 * - Ensure consistency across the codebase
 * - Make route changes easier (single source of truth)
 * - Enable type safety and autocomplete
 * - Prevent typos in route paths
 *
 * Usage:
 *   import { ROUTES } from '@/src/utils/routes'
 *   router.push(ROUTES.RECIPE_MANAGEMENT)
 *   router.push(ROUTES.RESTAURANT_DETAIL(123))
 */

/**
 * Base route paths
 */
export const ROUTES = {
  // Auth routes
  LOGIN: '/login',
  REGISTER: '/register',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD: '/reset-password',
  HOME: '/',
  NOT_FOUND: '/404',
  UNAUTHORIZED: '/401',
  SERVER_ERROR: '/500',
  FEATURE_UNAVAILABLE: '/feature-unavailable',

  // Recipe Management
  RECIPE_MANAGEMENT: '/app/recipe-mgmt',
  RECIPE_MANAGEMENT_ADD: '/app/recipe-mgmt/add',
  RECIPE_MANAGEMENT_EDIT: (id: string | number) => `/app/recipe-mgmt/${id}/edit`,

  /** Build recipe editor (Regular/Mini build flow) */
  RECIPE_MANAGEMENT_EDIT_BUILD: (id: string | number) => `/app/recipe-mgmt/${id}/edit-build`,

  /** Cafe Hola recipe editor */
  RECIPE_MANAGEMENT_EDIT_CAFE_HOLA: (id: string | number) => `/app/recipe-mgmt/${id}/edit-cafe-hola`,

  // Recipe Guide Ingredients
  RECIPE_GUIDE_INGREDIENTS: '/app/recipe-guide-ingredients',
  RECIPE_GUIDE_INGREDIENTS_MANAGEMENT: '/app/recipe-guide-ingredients',
  RECIPE_GUIDE_INGREDIENTS_ADD: '/app/recipe-guide-ingredients/add',
  RECIPE_GUIDE_INGREDIENTS_EDIT: (id: string | number) => `/app/recipe-guide-ingredients/${id}`,

  // Recipe Guide Equipments
  RECIPE_GUIDE_EQUIPMENTS: '/app/recipe-guide-equipments',
  RECIPE_GUIDE_EQUIPMENTS_ADD: '/app/recipe-guide-equipments/add',
  RECIPE_GUIDE_EQUIPMENTS_EDIT: (id: string | number) => `/app/recipe-guide-equipments/${id}`,

  // Food Safety Tags
  FOOD_SAFETY_TAGS: '/app/food-safety-tags',
  FOOD_SAFETY_TAGS_CREATE: '/app/food-safety-tags/create',
  FOOD_SAFETY_TAGS_EDIT: (id: string | number) => `/app/food-safety-tags/${id}`,

  // Portioning Tools
  PORTIONING_TOOLS: '/app/portioning-tools',
  PORTIONING_TOOLS_CREATE: '/app/portioning-tools/create',
  PORTIONING_TOOLS_EDIT: (id: string | number) => `/app/portioning-tools/${id}`,

  // Recipe Making Tips
  RECIPE_MAKING_TIPS: '/app/recipe-making-tips',
  RECIPE_MAKING_TIPS_CREATE: '/app/recipe-making-tips/create',
  RECIPE_MAKING_TIPS_EDIT: (id: string | number) => `/app/recipe-making-tips/${id}`,

  // Recipe QRG
  RECIPE_QRG: '/app/recipe-qrg',

  // Restaurant Management
  RESTAURANT: '/app/restaurant',
  RESTAURANT_ADD: '/app/restaurant/add',
  RESTAURANT_DETAIL: (id: string | number) => `/app/restaurant/${id}`,
  RESTAURANT_EQUIPMENT: (id: string | number) => `/app/restaurant/${id}/equipment`,
  RESTAURANT_TRADING_HOURS: (id: string | number) => `/app/restaurant/${id}/menu-services/trading-hours`,

  /** Redesigned trading hours (temporary + permanent V3 UI) */
  RESTAURANT_TRADING_HOURS_V3: (id: string | number) => `/app/restaurant/${id}/menu-services/trading-hours/v3`,
  RESTAURANT_READY_TO_ROLL: (id: string | number) => `/app/restaurant/${id}/ready-to-roll`,
  RESTAURANT_READY_TO_ROLL_ADD: (id: string | number) => `/app/restaurant/${id}/ready-to-roll/add`,
  RESTAURANT_READY_TO_ROLL_ADD_SUBMISSION: (id: string | number, daypart: string) =>
    `/app/restaurant/${id}/ready-to-roll/add/submission/${daypart}`,
  RESTAURANT_READY_TO_ROLL_VIEW: (id: string | number, slug: string) =>
    `/app/restaurant/${id}/ready-to-roll/view/${slug}`,
  RESTAURANT_READY_TO_ROLL_REPORT: (id: string | number) => `/app/restaurant/${id}/report-ready-to-roll`,
  RESTAURANT_RSS: (id: string | number) => `/app/restaurant/${id}/rss`,
  RESTAURANT_RSS_VIEW: (id: string | number, slug: string) => `/app/restaurant/${id}/rss/view/${slug}`,
  RESTAURANT_RSS_REPORT: (id: string | number) => `/app/restaurant/${id}/report-rss`,

  /** Per-restaurant training progress (mock UI until API exists) */
  RESTAURANT_TRAINING_DASHBOARD: (id: string | number) => `/app/restaurant/${id}/training-dashboard/`,

  /** Trainee schedule board: published templates in Unassigned + day columns (mock) */
  RESTAURANT_TRAINING_TRAINEE_BOARD: (restaurantId: string | number, traineeId: string | number) =>
    `/app/restaurant/${restaurantId}/training-dashboard/trainee/${traineeId}`,

  // Franchise Hub
  FRANCHISE_HUB: '/app/franchise-hub',
  FRANCHISE_HUB_ADD_BASE: '/app/franchise-hub/add',
  FRANCHISE_HUB_DETAIL: (id: string | number) => `/app/franchise-hub/${id}`,
  FRANCHISE_HUB_ADD: (reviewId: string | number) => `/app/franchise-hub/add/${reviewId}`,
  FRANCHISE_HUB_REVIEW_SUBMISSION: (reviewId: string | number) => `/app/franchise-hub/review-submission/${reviewId}`,
  FRANCHISE_HUB_REVIEW_SIGNOFF: (reviewId: string | number) => `/app/franchise-hub/review-signoff/${reviewId}`,

  // User Management
  USER_MANAGEMENT: '/app/user-management',
  USER_MANAGEMENT_ROLES: '/app/user-management/roles',
  USER_MANAGEMENT_PERMISSIONS: '/app/user-management/permissions',
  USER_MANAGEMENT_PROFILE: (userId?: string | number) =>
    userId ? `/app/user-management/profile/?userId=${userId}` : '/app/user-management/profile',

  // Customer Portal
  CUSTOMER_PORTAL: '/app/customer-portal',
  CUSTOMER_PORTAL_USERS: '/app/customer-portal/users',
  CUSTOMER_PORTAL_USER_DETAIL: (userId: string | number) => `/app/customer-portal/users/${userId}`,
  CUSTOMER_PORTAL_ORDER_DETAIL: (orderId: string | number) => `/app/customer-portal/orders/${orderId}`,

  // Marketing Portal
  MARKETING_PORTAL: '/app/marketing-portal',
  MARKETING_PORTAL_OFFERS: '/app/marketing-portal/offers',
  MARKETING_PORTAL_OFFERS_CREATE: '/app/marketing-portal/offers/create',
  MARKETING_PORTAL_OFFERS_CREATE_LOYALTY: '/app/marketing-portal/offers/create/loyalty',
  MARKETING_PORTAL_OFFERS_DETAIL: (id: string | number) => `/app/marketing-portal/offers/${id}`,
  MARKETING_PORTAL_OFFERS_EDIT: (id: string | number) => `/app/marketing-portal/offers/${id}/edit`,
  MARKETING_PORTAL_OFFERS_EDIT_LOYALTY: (id: string | number) => `/app/marketing-portal/offers/${id}/edit/loyalty`,
  MARKETING_PORTAL_OFFERS_ADMIN_EDIT: (id: string | number) => `/app/marketing-portal/offers/${id}/admin-edit`,

  // Training Planner (mock UI — templates & plans)
  TRAINING_PLANNER_TEMPLATES: '/app/training-planner/templates',
  TRAINING_PLANNER_CATALOG: '/app/training-planner/catalog',

  /** @deprecated Use TRAINING_PLANNER_CATALOG */
  TRAINING_PLANNER_COLOUR_REF: '/app/training-planner/catalog',

  /** @deprecated Use TRAINING_PLANNER_CATALOG */
  TRAINING_PLANNER_ICON_SETTINGS: '/app/training-planner/catalog',
  TRAINING_PLANNER_TEMPLATES_CREATE: '/app/training-planner/templates/create',
  TRAINING_PLANNER_TEMPLATE_DETAIL: (id: string | number) => `/app/training-planner/templates/${id}`,
  TRAINING_PLANNER_TEMPLATE_EDIT: (id: string | number) => `/app/training-planner/templates/${id}/edit`,

  // Multi-store training progress reporting
  REPORTING_TRAINING_PLANNER: '/app/reporting/training-planner',

  // Per-restaurant training report table
  RESTAURANT_REPORT_TRAINING_PLANNER: (id: string | number) => `/app/restaurant/${id}/report-training-planner`,

  // FAQ
  FAQ: '/app/faq',
  FAQ_ADD: '/app/faq/add',
  FAQ_DETAIL: (id: string | number) => `/app/faq/${id}`,
  FAQ_CATEGORY: (categoryId: string | number) => `/app/faq/category/${categoryId}`,

  // Products
  PRODUCTS: '/app/products',
  PRODUCTS_DETAIL: (id: string | number) => `/app/products/${id}`,
  PRODUCTS_PRICING: '/app/products/pricing',
  PRODUCTS_EDIT: (id: string | number) => `/app/products/edit/${id}`,
  PRODUCTS_EDIT_CONTAINER: (id: string | number) => `/app/products/edit-container/${id}`,
  PRODUCTS_EDIT_MODIFIER: (id: string | number) => `/app/products/edit-modifier/${id}`,

  // Submissions
  SUBMISSIONS: '/app/submissions',
  SUBMISSIONS_READY_TO_ROLL: '/app/submissions/ready-to-roll',
  SUBMISSIONS_RSS: '/app/submissions/rss',
  SUBMISSIONS_CLEANING_SHEET: '/app/submissions/cleaning-sheet',

  // History Logs
  HISTORY_LOGS: '/app/history-logs',
  HISTORY_LOGS_DETAIL: (logId: string | number) => `/app/history-logs/${logId}`,

  // Disabled Ingredients
  DISABLED_INGREDIENTS: '/app/disabled-ingredients',
  DISABLED_INGREDIENTS_REPORTS: '/app/disabled-ingredients/reports',

  /** Use in emails: link to reports page with ?key=... so user can download after logging in. Base URL must be the app origin (e.g. window.location.origin). */
  DISABLED_INGREDIENTS_REPORTS_DOWNLOAD_LINK: (key: string) =>
    `/app/disabled-ingredients/reports?key=${encodeURIComponent(key)}`,
  DISABLED_INGREDIENTS_ADD: '/app/disabled-ingredients/add',
  DISABLED_INGREDIENTS_EDIT: (ingredientId: string | number) => `/app/disabled-ingredients/edit/${ingredientId}`,

  // Order Management
  ORDER_MANAGEMENT: '/app/order-management',

  // Operations
  OPERATIONS_STORE_SURCHARGES: '/app/operations/store-surcharges',
  OPERATIONS_MASTER_PRICING_LIST: '/app/operations/master-pricing-list',

  // Menu Equipments
  MENU_EQUIPMENTS: '/app/menu-equipments',
  ADMIN_TOOLS_INGREDIENTS: '/app/admin-tools-ingredients',

  /** Admin Tools → Feature experiences (rollout definitions + preference audit) */
  ADMIN_FEATURE_EXPERIENCES: '/app/admin-tools/feature-experiences',
  CRON_JOBS: '/app/cron-jobs',

  // Insights
  RESTAURANT_INSIGHTS: '/app/restaurant-insights',
  AI_INSIGHTS: '/app/ai-insights',

  // Pages
  ACCOUNT_SETTINGS: (section?: string) =>
    section ? `/pages/account-settings/${section.toLowerCase()}` : '/pages/account-settings',
  HELP_CENTER: (slug?: string, subcategory?: string) => {
    if (slug && subcategory) {
      return `/pages/help-center/${slug}/${subcategory}`
    }
    if (slug) {
      return `/pages/help-center/${slug}`
    }

    return '/pages/help-center'
  }
} as const

/**
 * Helper function to build query string routes
 */
export const buildRouteWithQuery = (baseRoute: string, queryParams: Record<string, string | number | undefined>) => {
  const params = new URLSearchParams()
  Object.entries(queryParams).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      params.append(key, String(value))
    }
  })
  const queryString = params.toString()

  return queryString ? `${baseRoute}?${queryString}` : baseRoute
}

/**
 * Type-safe route builder
 */
export type RouteKey = keyof typeof ROUTES

/**
 * Get route by key (for dynamic route access)
 */
export const getRoute = (key: RouteKey): string => {
  const route = ROUTES[key]
  if (typeof route === 'function') {
    throw new Error(`Route ${key} requires parameters. Use the function directly instead.`)
  }

  return route
}
